import React, { useState, useMemo, useEffect, forwardRef } from 'react'
import dynamic from 'next/dynamic'
import { IMaskInput } from 'react-imask'
import type { FieldError as HookFieldError } from 'react-hook-form'

import { COUNTRY_CODES, CountryCode } from '@/constants/countryCodes'
import { cn } from '@/lib/utils'
import FieldError from '@/components/livecontact/FieldError'

const CountryCodeDropdown = dynamic(() => import('./CountryCodeDropdown'), { ssr: false })

type PhoneFieldProps = {
  className?: string
  classInput?: string
  classLabel?: string
  showError?: boolean
  label?: string
  note?: string
  value?: string
  defaultValue?: string
  onChange?: (event: any) => void
  onBlur?: () => void
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void
  required?: boolean
  disabled?: boolean
  error?: HookFieldError
  classLabelInner?: string
  readOnly?: boolean
  defaultCountryCode?: string
  classInnerField?: string
  onCountryChange?: (country: CountryCode) => void
}

const PhoneField = forwardRef<HTMLInputElement, PhoneFieldProps>(
  (
    {
      className,
      classInput,
      classLabel,
      label,
      note,
      value,
      onChange,
      onKeyDown,
      onBlur,
      required,
      readOnly = false,
      disabled,
      error,
      classLabelInner,
      defaultCountryCode = '+1',
      onCountryChange,
      classInnerField,
      showError = true,
    },
    ref
  ) => {
    const [selectedCountry, setSelectedCountry] = useState<CountryCode>(
      COUNTRY_CODES.find((c) => c.dialCode === defaultCountryCode) || COUNTRY_CODES[0]
    )
    const [phoneNumber, setPhoneNumber] = useState('')

    // Parse value to extract country code and phone number
    useEffect(() => {
      if (value) {
        // Find matching country by checking if value starts with the dial code
        const matchedCountry = COUNTRY_CODES.find((country) => value.startsWith(country.dialCode))

        if (matchedCountry) {
          setSelectedCountry(matchedCountry)
          // Extract phone number without country code
          const phone = value.slice(matchedCountry.dialCode.length).trim()
          setPhoneNumber(phone)
        } else {
          // If no match found, treat entire value as phone number
          setPhoneNumber(value)
        }
      } else {
        setPhoneNumber('')
      }
    }, [value])

    const handleCountrySelect = (country: CountryCode) => {
      setSelectedCountry(country)
      onCountryChange?.(country)

      if (onChange) {
        const fullNumber = country.dialCode + phoneNumber.replace(/\D/g, '')
        onChange(fullNumber)
      }
    }
    const handlePhoneChange = (maskedValue: string) => {
      setPhoneNumber(maskedValue)

      if (onChange) {
        const cleanNumber = maskedValue.replace(/\D/g, '')
        const fullNumber = cleanNumber ? selectedCountry.dialCode + cleanNumber : ''
        onChange(fullNumber)
      }
    }
    // Generate dynamic placeholder and mask
    const dynamicPlaceholder = useMemo(
      () => selectedCountry.placeholder || 'Phone number',
      [selectedCountry.placeholder]
    )

    const dynamicMask = useMemo(
      () => selectedCountry.mask || '000000000000000',
      [selectedCountry.mask]
    )

    return (
      <div className={cn(className)}>
        {label && (
          <div
            className={cn(
              'mb-1.5 flex text-[12px] font-medium leading-[1.4] tracking-[-0.2px] text-ink-3',
              classLabelInner
            )}
          >
            <span className={classLabel}>{label}</span>
          </div>
        )}
        <div className={cn('flex gap-2', classInnerField)}>
          {/* Country code chip */}
          <CountryCodeDropdown
            selectedCountry={selectedCountry}
            onCountrySelect={handleCountrySelect}
            disabled={disabled}
            classDropdown={classInput}
          />

          {/* Masked number input — same 32px surface as the Field component */}
          <IMaskInput
            mask={dynamicMask}
            value={phoneNumber}
            onAccept={(value) => {
              handlePhoneChange(value)
            }}
            inputRef={ref}
            onBlur={onBlur}
            onKeyDown={onKeyDown}
            disabled={disabled}
            readOnly={readOnly}
            unmask={false}
            className={cn(
              'h-8 min-w-0 flex-1 rounded-[8px] bg-surface px-2 text-[14px] leading-[1.4] tracking-[-0.2px] text-ink shadow-input outline-none',
              'transition-shadow duration-[160ms] ease-out-strong placeholder:text-ink-4',
              'focus:shadow-[0_4px_4px_-3px_rgba(0,0,0,0.1),0_0_0_1px_rgb(var(--lc-brand)),0_0_0_4px_rgb(var(--lc-brand)/0.14)]',
              'disabled:cursor-not-allowed disabled:opacity-60',
              error && 'shadow-[0_4px_4px_-3px_rgba(0,0,0,0.1),0_0_0_1px_#D33]',
              classInput
            )}
            type="tel"
            placeholder={dynamicPlaceholder}
            required={required}
            aria-invalid={!!error}
          />
        </div>
        {note && (
          <p className="mt-1.5 text-[12px] leading-[1.4] tracking-[-0.2px] text-ink-4">{note}</p>
        )}
        {showError && <FieldError message={error?.message} />}
      </div>
    )
  }
)

PhoneField.displayName = 'PhoneField'

export default PhoneField
